import React, { useEffect } from 'react';
import { OrbData, BudgetItem } from '../types/FinancialData';

interface DetailModalProps {
  orb: OrbData | null;
  budgetData: BudgetItem[];
  onClose: () => void;
  isOpen: boolean;
}

export const DetailModal: React.FC<DetailModalProps> = ({
  orb,
  budgetData,
  onClose,
  isOpen
}) => {
  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  if (!isOpen || !orb) return null;

  const subTagLabel = (tag: string) => {
    if (tag === 'meet-additional-demand') return 'Meet Additional Demand';
    if (tag === 'improve-level-of-service') return 'Improve Level of Service';
    if (tag === 'replace-existing-assets') return 'Replace Existing Assets';
    return tag;
  };
  const subTagStyle = (tag: string) => {
    if (tag === 'meet-additional-demand') return 'bg-amber-100 text-amber-800';
    if (tag === 'improve-level-of-service') return 'bg-green-100 text-green-800';
    if (tag === 'replace-existing-assets') return 'bg-blue-100 text-blue-800';
    return 'bg-gray-100 text-gray-800';
  };

  // Shade a hex colour at a given alpha — used for subcategory segments within a bar
  const hexToRgba = (hex: string, alpha: number) => {
    const r = parseInt(hex.slice(1, 3), 16);
    const g = parseInt(hex.slice(3, 5), 16);
    const b = parseInt(hex.slice(5, 7), 16);
    return `rgba(${r},${g},${b},${alpha})`;
  };
  // Consistent shade order: darkest → lightest
  const TAG_SHADE_ORDER = ['replace-existing-assets', 'improve-level-of-service', 'meet-additional-demand'];
  const TAG_ALPHAS = [1.0, 0.62, 0.32];
  const subTagShade = (tag: string, barHex: string): string => {
    const idx = TAG_SHADE_ORDER.indexOf(tag);
    const alpha = idx >= 0 ? TAG_ALPHAS[idx] : 0.5;
    return hexToRgba(barHex, alpha);
  };
  // Match the existing bar colour logic
  const BAR_COLORS = {
    dap: '#f59e0b',
    increase: '#22c55e',
    decrease: '#f87171',
    neutral: '#60a5fa',
  };

  // Find the project data
  const projectData = budgetData.find(item => item.id === orb.id);
  
  if (!projectData) {
    return null;
  }

  // Convert yearlyAmounts to array format for charts
  const orbHistory = Object.entries(projectData.yearlyAmounts)
    .map(([yearStr, amount]) => ({
      year: yearStr,
      amount: amount,
      yearNum: parseInt(yearStr.match(/(\d{4})/)?.[1] || '2023')
    }))
    .sort((a, b) => a.yearNum - b.yearNum); // all years, including zeros

  const dapAmount = projectData.yearlyAmounts['2026/2027'] ?? 0;
  // 8-year total: 2026/2027 → 2033/2034 only (excludes the prior year 2025/2026)
  const planYears = orbHistory.filter(item => item.year !== '2025/2026');
  const totalSpend = planYears.reduce((sum, item) => sum + item.amount, 0);
  const nonZeroYears = planYears.filter(item => item.amount > 0);
  const avgSpend = nonZeroYears.length > 0 ? totalSpend / nonZeroYears.length : 0;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-end sm:items-start justify-center z-50 p-0 sm:pt-4 sm:px-4">
      <div className="bg-white rounded-t-2xl sm:rounded-xl max-w-4xl w-full max-h-[96vh] sm:max-h-[calc(100vh-2rem)] overflow-y-auto shadow-2xl">
        <div className="bg-gradient-to-r from-blue-600 to-purple-600 px-4 sm:px-6 py-3 flex gap-3 sticky top-0 z-10">
          {/* Title + tags */}
          <div className="flex-1 min-w-0">
            <div className="flex items-start gap-2 mb-1.5">
              <svg className="w-5 h-5 mt-0.5 flex-shrink-0 text-white" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd"/>
              </svg>
              <h2 className="text-base sm:text-lg font-bold leading-snug text-white">{orb.name}</h2>
            </div>
            <div className="flex flex-wrap gap-1.5">
              <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-white/20 text-white">
                {orb.category.split('-').map((w: string) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}
              </span>
              {projectData.subcategories
                ? [...new Set(projectData.subcategories.map(s => s.subcategoryTag).filter(Boolean))].map(tag => (
                    <span key={tag} className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-white/20 text-white">
                      {subTagLabel(tag!)}
                    </span>
                  ))
                : projectData.subcategoryTag && (
                    <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-white/20 text-white">
                      {subTagLabel(projectData.subcategoryTag)}
                    </span>
                  )
              }
              {projectData.projectType && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-white/20 text-white">
                  {projectData.projectType === 'capex' ? 'Capital' : 'Operating'}
                </span>
              )}
            </div>
          </div>
          <button
            onClick={onClose}
            className="text-white hover:text-gray-200 transition-colors flex-shrink-0 p-1 self-start"
          >
            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
            </svg>
          </button>
        </div>

        <div className="p-4 sm:p-5">
          {projectData.description && (
            <p className="text-sm text-gray-600 bg-blue-50 border border-blue-100 rounded-lg px-4 py-3 mb-4">
              {projectData.description}
            </p>
          )}

          <div className="grid grid-cols-3 gap-2 sm:gap-3 mb-4">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 py-2.5 rounded-xl text-center">
              <h3 className="text-xs font-semibold opacity-90 mb-0.5">DAP 2026-27</h3>
              <p className="text-base sm:text-xl font-bold">${(dapAmount / 1000000).toFixed(2)}M</p>
            </div>
            <div className="bg-gradient-to-r from-green-500 to-green-600 text-white px-3 py-2.5 rounded-xl text-center">
              <h3 className="text-xs font-semibold opacity-90 mb-0.5">8-Year Total</h3>
              <p className="text-base sm:text-xl font-bold">${(totalSpend / 1000000).toFixed(2)}M</p>
            </div>
            <div className="bg-gradient-to-r from-purple-500 to-purple-600 text-white px-3 py-2.5 rounded-xl text-center">
              <h3 className="text-xs font-semibold opacity-90 mb-0.5">Avg / Year</h3>
              <p className="text-base sm:text-xl font-bold">${(avgSpend / 1000000).toFixed(2)}M</p>
            </div>
          </div>

          <div className="bg-gray-50 rounded-xl p-3 sm:p-4">
            <h3 className="text-sm font-bold text-gray-800 mb-3 flex items-center">
              <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M2 10a8 8 0 018-8v8h8a8 8 0 11-16 0z"/>
                <path d="M12 2.252A8.014 8.014 0 0117.748 8H12V2.252z"/>
              </svg>
              Year-by-Year Spending
            </h3>
            
            {/* Horizontal bar chart */}
            <div className="bg-white rounded-lg border p-4 space-y-2">
              {orbHistory.map((item, index) => {
                const maxAmount = Math.max(...orbHistory.map(h => h.amount), 1);
                const barPct = (item.amount / maxAmount) * 100;
                const prevAmount = index > 0 ? orbHistory[index - 1].amount : item.amount;
                const change = index > 0 && prevAmount > 0 ? ((item.amount - prevAmount) / prevAmount) * 100 : 0;
                const isIncrease = change > 0;
                const isDecrease = change < 0;
                const isDap = item.year === '2026/2027';
                const isPrior = item.year === '2025/2026';
                const shortYear = item.year.split('/')[1];

                // The base bar colour for this year (matches non-merged orb logic)
                const barHex = isDap ? BAR_COLORS.dap
                  : isPrior ? BAR_COLORS.neutral
                  : isIncrease ? BAR_COLORS.increase
                  : isDecrease ? BAR_COLORS.decrease
                  : BAR_COLORS.neutral;

                // Build subcategory segments if this item is merged
                const segments = projectData.subcategories
                  ? projectData.subcategories
                      .map(sub => ({
                        tag: sub.subcategoryTag!,
                        amount: sub.yearlyAmounts[item.year] ?? 0,
                      }))
                      .filter(s => s.amount > 0)
                  : null;

                return (
                  <div key={item.year} className="flex items-center gap-2">
                    {/* Year label */}
                    <div className={`text-xs font-bold w-10 flex-shrink-0 text-right ${isDap ? 'text-amber-700' : 'text-gray-600'}`}>
                      {shortYear}
                    </div>

                    {/* Bar track */}
                    <div className="flex-1 bg-gray-100 rounded h-5 overflow-hidden">
                      {item.amount > 0 && (
                        <div
                          className="h-full flex rounded overflow-hidden transition-all duration-500"
                          style={{ width: `${Math.max(barPct, 2)}%` }}
                        >
                          {segments ? (
                            segments.map(seg => (
                              <div
                                key={seg.tag}
                                style={{
                                  width: `${(seg.amount / item.amount) * 100}%`,
                                  backgroundColor: subTagShade(seg.tag, barHex),
                                }}
                                title={`${subTagLabel(seg.tag)}: $${(seg.amount / 1000000).toFixed(2)}M`}
                              />
                            ))
                          ) : (
                            <div className="h-full w-full" style={{ backgroundColor: barHex }} />
                          )}
                        </div>
                      )}
                    </div>

                    {/* Amount label */}
                    <div className="text-xs font-bold text-gray-800 w-16 flex-shrink-0 text-right">
                      {item.amount > 0 ? `$${(item.amount / 1000000).toFixed(2)}M` : '—'}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Subcategory legend (only for merged items) — swatches use DAP gold shading as reference */}
            {projectData.subcategories && (
              <div className="mt-3 flex flex-wrap gap-3">
                {[...new Set(projectData.subcategories.map(s => s.subcategoryTag).filter(Boolean))].map(tag => (
                  <div key={tag} className="flex items-center gap-1.5">
                    <div
                      className="w-3 h-3 rounded-sm flex-shrink-0"
                      style={{ backgroundColor: subTagShade(tag!, BAR_COLORS.dap) }}
                    />
                    <span className="text-xs text-gray-600">{subTagLabel(tag!)}</span>
                  </div>
                ))}
              </div>
            )}

            {/* Legend */}
            <div className="mt-3 space-y-0.5 text-xs text-gray-500">
              <p><span className="font-semibold text-gray-600">2026</span> = Current financial year, ending 30 June 2026</p>
              <p><span className="font-semibold text-amber-700">2027</span> = Draft Annual Plan budget</p>
              <p><span className="font-semibold text-gray-600">2028+</span> = Forecasts from the Long-Term Plan</p>
            </div>
          </div>
        </div>
        
        <div className="bg-gray-100 px-4 sm:px-6 py-3 sm:py-4 flex justify-end">
          <button
            onClick={onClose}
            className="flex items-center px-6 py-3 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-all font-medium"
          >
            <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"/>
            </svg>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};